import React, { useState, useEffect, useRef } from 'react';
import { io } from 'socket.io-client';
import { BACKEND_URL } from '../config';
import './Console.css';

const socket = io(BACKEND_URL);

function Console() {
  const [logs, setLogs] = useState([]);
  const [command, setCommand] = useState('');
  const [platform, setPlatform] = useState('twitch-main');
  const logsEndRef = useRef(null);

  useEffect(() => {
    // Listen for service logs
    socket.on('service:log', (data) => {
      addLog(`[${data.service}] [${data.level.toUpperCase()}] ${data.message}`, data.level);
    });

    // Listen for chat messages
    socket.on('chat:message', (data) => {
      addLog(`[${data.platform}] ${data.username}: ${data.message}`, 'chat');
    });

    // Add welcome message
    addLog('Console bereit. Gib Commands ein oder beobachte die Logs.', 'info');

    return () => {
      socket.off('service:log');
      socket.off('chat:message');
    };
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [logs]);

  const scrollToBottom = () => {
    logsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const addLog = (message, level = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs(prev => [...prev, { timestamp, message, level }]);
  };

  const executeCommand = (e) => {
    e.preventDefault();
    if (!command.trim()) return;

    addLog(`> ${command}`, 'command');

    // Parse command
    const parts = command.trim().split(' ');
    const cmd = parts[0].toLowerCase();
    const args = parts.slice(1);

    switch (cmd) {
      case 'clear':
        setLogs([]);
        break;

      case 'help':
        addLog('Verfügbare Commands:', 'info');
        addLog('  clear - Console leeren', 'info');
        addLog('  help - Diese Hilfe anzeigen', 'info');
        addLog('  say <message> - Nachricht im Chat senden', 'info');
        addLog('  service start <name> - Service starten', 'info');
        addLog('  service stop <name> - Service stoppen', 'info');
        addLog('  service restart <name> - Service neustarten', 'info');
        addLog('  obs scene <name> - OBS Scene wechseln', 'info');
        addLog('  obs stream start/stop - Stream starten/stoppen', 'info');
        addLog('  platform <name> - Plattform wechseln (twitch-main, twitch-247, youtube, discord)', 'info');
        addLog('  restart backend - Backend neu starten', 'info');
        addLog('  restart frontend - Frontend neu starten', 'info');
        break;

      case 'say':
        if (args.length === 0) {
          addLog('Fehler: Keine Nachricht angegeben', 'error');
        } else {
          const message = args.join(' ');
          socket.emit('console:sendMessage', { platform, message });
          addLog(`Nachricht gesendet an ${platform}: ${message}`, 'success');
        }
        break;

      case 'service':
        if (args.length < 2) {
          addLog('Fehler: service <start|stop|restart> <name>', 'error');
        } else {
          const action = args[0];
          const serviceName = args[1];
          socket.emit(`service:${action}`, serviceName);
          addLog(`Service ${serviceName} ${action}...`, 'info');
        }
        break;

      case 'obs':
        if (args.length < 2) {
          addLog('Fehler: obs <scene|stream> <...>', 'error');
        } else {
          const subCmd = args[0];
          if (subCmd === 'scene') {
            const sceneName = args.slice(1).join(' ');
            socket.emit('obs:setScene', sceneName);
            addLog(`OBS Scene gewechselt zu: ${sceneName}`, 'success');
          } else if (subCmd === 'stream') {
            const action = args[1];
            if (action === 'start') {
              socket.emit('obs:startStreaming');
              addLog('OBS Stream gestartet', 'success');
            } else if (action === 'stop') {
              socket.emit('obs:stopStreaming');
              addLog('OBS Stream gestoppt', 'success');
            }
          }
        }
        break;

      case 'platform':
        if (args.length === 0) {
          addLog(`Aktuelle Plattform: ${platform}`, 'info');
        } else {
          const newPlatform = args[0];
          setPlatform(newPlatform);
          addLog(`Plattform gewechselt zu: ${newPlatform}`, 'success');
        }
        break;

      case 'restart':
        if (args.length === 0) {
          addLog('Fehler: restart <backend|frontend>', 'error');
        } else {
          const target = args[0].toLowerCase();
          if (target === 'backend') {
            addLog('⏳ Backend wird neu gestartet...', 'info');
            fetch(`${BACKEND_URL}/api/system-control/restart/backend`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              }
            })
              .then(res => {
                if (!res.ok) {
                  throw new Error(`HTTP ${res.status}`);
                }
                return res.json();
              })
              .then(data => {
                addLog(`✅ ${data.message}`, 'success');
                addLog('⚠️ Verbindung wird in wenigen Sekunden unterbrochen...', 'warning');
                addLog('💡 Warte 5-10 Sekunden und lade die Seite neu.', 'info');
              })
              .catch(err => {
                addLog(`❌ Backend nicht erreichbar. Starte manuell mit "npm start"`, 'error');
                console.error('Restart error:', err);
              });
          } else if (target === 'frontend') {
            addLog('ℹ️ Frontend muss manuell neu gestartet werden:', 'info');
            addLog('  1. Drücke Strg+C im Frontend-Terminal', 'info');
            addLog('  2. Führe "npm start" im frontend Ordner aus', 'info');
          } else {
            addLog('Fehler: Ungültiges Ziel. Nutze: restart <backend|frontend>', 'error');
          }
        }
        break;

      default:
        addLog(`Unbekannter Command: ${cmd}. Tippe 'help' für Hilfe.`, 'error');
    }

    setCommand('');
  };

  const getLevelClass = (level) => {
    switch (level) {
      case 'error': return 'log-error';
      case 'warning': return 'log-warning';
      case 'success': return 'log-success';
      case 'command': return 'log-command';
      case 'chat': return 'log-chat';
      default: return 'log-info';
    }
  };

  return (
    <div className="console-page">
      <div className="page-header">
        <h2>🖥️ Console</h2>
        <div className="console-info">
          <span className="platform-indicator">Plattform: {platform}</span>
          <button className="btn-secondary" onClick={() => setLogs([])}>
            🗑️ Clear
          </button>
        </div>
      </div>

      <div className="console-container">
        <div className="console-logs">
          {logs.map((log, index) => (
            <div key={index} className={`log-entry ${getLevelClass(log.level)}`}>
              <span className="log-timestamp">[{log.timestamp}]</span>
              <span className="log-message">{log.message}</span>
            </div>
          ))}
          <div ref={logsEndRef} />
        </div>

        <form className="console-input" onSubmit={executeCommand}>
          <span className="console-prompt">$</span>
          <input
            type="text"
            value={command}
            onChange={(e) => setCommand(e.target.value)}
            placeholder="Command eingeben... (tippe 'help' für Hilfe)"
            autoFocus
          />
          <button type="submit" className="btn-primary">
            ▶ Execute
          </button>
        </form>
      </div>
    </div>
  );
}

export default Console;
